import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'axios';
import BitaSelect from '../../../../../components/BitaSelect/BitaSelect';
import BitaInput from '../../../../../components/BitaInput/BitaInput';
import Switcher from '../../../../../bitaComponents/Switcher/Switcher';
import BitaButton from '../../../../../bitaComponents/BitaButton/BitaButton';
import VisualizationModal from '../../../withholding-tax/VisualizationModal';
import { useFieldRefContext } from '../../../../../bitaHooks';
import {
  getDateString,
  isDateBeforeOrToday,
  doesSymbolExist,
  updateWithholdingTaxTemplatesData,
  parseDecimalInputValue,
} from '../../../utils';
import { formatDateCalendar } from '../../../../../utils/utils';
import { URL_CONSTANTS } from '../../../../../api-data-mapping/url-constants';
import {
  CURRENCIES_ARRAY,
  BITA_WITHHOLDING_TAX,
  DATA_CALCULATION_TYPE,
  DATA_CALCULATION,
  BULK_CALCULATION_TYPE_OPTIONS,
  DATA_CALCULATION_SETUP,
  DATA_CALCULATION_SETUP_VALUES,
  MAX_LAUNCH_DATE,
} from '../../../utils/constants';
import BitaCalendarDate from '../../../../../components/BitaCalendar/BitaCalendarDate';
import styles from './ParametersForm.module.scss';
import indexBuilderStyles from '../../../index-builder.module.css';
import portforlioStyles from '../../../PortfolioBasketCalculation.module.scss';

const FORM_INPUT_TEXTS = {
  labels: {
    description: 'DESCRIPTION',
    divisorDetermination: 'Divisor Determination',
  },
  placeholders: {
    descriptions: 'Enter description',
    divisorDetermination: 'Select divisor determination',
  },
  required: {
    divisorDeterminationPoint: 'Divisor determination point is required',
  },
};

const DATA_DIVISOR_DETERMINATION_SETUP = [
  { value: 'Previous EOD', id: 'PREVIOUS_EOD' },
  { value: 'Immediately', id: 'IMMEDIATELY' },
];

const ParametersForm = ({
  setBasketsWithoutCoverage,
  shouldSendLaunchDate,
  setSelectedIndex,
  isSubmitBasket,
  withholdingTaxTemplateData,
  setWithholdingTaxTemplateData,
  selectedBasketsData,
  indexMetadata,
  setIndexMetadata,
  validateFields,
  errors,
  existsCashConstituentsInMetadata,
  enableDivisorOrStartingValue,
  setEnableDivisorOrStartingValue,
  calculationZoneOptions,
  calculationZoneMap,
}) => {
  const user = useSelector(state => state.auth);
  const tickerInputRef = useRef(null);
  const { registerFieldRef } = useFieldRefContext();
  const [automaticallyAdjustDivisor, setAutomaticallyAdjustDivisor] = useState(
    indexMetadata?.automatically_adjust_divisor ?? true,
  );
  const [isWithholdingModalOpen, setIsWithholdingModalOpen] = useState(false);
  const validateAndHandleNameChange = useCallback(e => {
    setIndexMetadata('name', e.target.value);
  }, []);
  const handleDescriptionChange = useCallback(e => {
    setIndexMetadata('description', e.target.value);
  }, []);

  const isInvalidBorderCase = useMemo(
    () =>
      enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusStarting &&
      indexMetadata.divisor_determination_setup === 'IMMEDIATELY' &&
      indexMetadata?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY,
    [
      enableDivisorOrStartingValue,
      indexMetadata?.symbol,
      indexMetadata?.divisor_determination_setup,
      indexMetadata?.calculation_setup,
    ],
  );

  const launchPointValue = useMemo(() => {
    return indexMetadata?.launch_point
      ? {
          value: DATA_CALCULATION_TYPE[indexMetadata?.launch_point],
          id: indexMetadata?.launch_point,
          name: indexMetadata?.launch_point,
        }
      : '';
  }, [indexMetadata?.launch_point]);

  const launchPointData = useMemo(() => {
    return DATA_CALCULATION.map(item => ({
      ...item,
      disabled:
        item.value === DATA_CALCULATION_SETUP_VALUES.EOD &&
        enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusStarting &&
        indexMetadata?.divisor_determination_setup === 'IMMEDIATELY',
    }));
  }, [
    enableDivisorOrStartingValue,
    indexMetadata?.symbol,
    indexMetadata?.divisor_determination_setup,
  ]);

  const divisorDeterminationSetupValue = useMemo(() => {
    return validateFields('divisor_determination_setup', indexMetadata?.divisor_determination_setup)
      ? DATA_DIVISOR_DETERMINATION_SETUP.find(
          item => item.id === indexMetadata?.divisor_determination_setup,
        )
      : '';
  }, [indexMetadata?.divisor_determination_setup]);

  const calculationZoneValue = useMemo(() => {
    const selectedZone = indexMetadata?.calculation_zone;
    if (!selectedZone || !calculationZoneMap?.has(selectedZone)) return '';

    return {
      id: selectedZone,
      value: calculationZoneMap.get(selectedZone)?.public_name,
    };
  }, [calculationZoneMap, indexMetadata?.calculation_zone]);

  const currencyValue = useMemo(() => {
    return indexMetadata?.currency
      ? {
          value: indexMetadata?.currency,
          id: indexMetadata?.currency,
        }
      : '';
  }, [indexMetadata?.currency]);

  const calculationTypeValue = useMemo(() => {
    return indexMetadata?.calculation_type
      ? {
          value: indexMetadata?.calculation_type.replace('_', ' '),
          id: indexMetadata?.calculation_type,
        }
      : '';
  }, [indexMetadata?.calculation_type]);

  const withholdingTaxIdValue = useMemo(() => {
    return indexMetadata?.withholding_tax_id
      ? {
          value: withholdingTaxTemplateData?.find(
            item => item.id === indexMetadata?.withholding_tax_id,
          )?.withholding_tax_name,
          id: indexMetadata?.withholding_tax_id,
        }
      : {
          value: BITA_WITHHOLDING_TAX.withholding_tax_name,
          id: BITA_WITHHOLDING_TAX.withholding_tax_name,
        };
  }, [indexMetadata?.withholding_tax_id]);

  const calculationSetupValue = useMemo(() => {
    return indexMetadata?.calculation_setup
      ? {
          value: indexMetadata?.calculation_setup.replace('_', ' '),
          id: indexMetadata?.calculation_setup,
        }
      : '';
  }, [indexMetadata?.calculation_setup]);

  const decrementValuePercentageValue = useMemo(() => {
    return indexMetadata?.additional_settings?.decrement_value_percentage
      ? indexMetadata?.additional_settings?.decrement_value_percentage * 100
      : '';
  }, [indexMetadata?.additional_settings?.decrement_value_percentage]);

  const currenciesArray = useMemo(() => {
    return CURRENCIES_ARRAY.map(item => {
      return { value: item, id: item };
    });
  }, [CURRENCIES_ARRAY]);

  useEffect(() => {
    // todo [DM251017], find a better way to focus the input after the symbol change
    if (isSubmitBasket && tickerInputRef.current) {
      tickerInputRef.current.focus();
    }
  }, [indexMetadata?.symbol]);

  useEffect(() => {
    if (isInvalidBorderCase) {
      setIndexMetadata('launch_point', 'IMMEDIATE');
    }
  }, [isInvalidBorderCase]);

  // Placeholder for missing handlers
  const handleCurrencySelect = value => {
    setIndexMetadata('currency', value);
  };

  const handleReturnTypeSelect = value => {
    setIndexMetadata('calculation_type', value);
  };

  const handleTickerChange = useCallback(
    (oldSymbol, rawNewSymbol) => {
      const newSymbol = rawNewSymbol?.toUpperCase?.() ?? '';
      if (doesSymbolExist(newSymbol, selectedBasketsData)) {
        return;
      }
      setSelectedIndex(newSymbol);
      setIndexMetadata('symbol', newSymbol);
      setBasketsWithoutCoverage(prev => prev.filter(item => item !== oldSymbol));
      setEnableDivisorOrStartingValue(prev => {
        const nextState = { ...prev };
        nextState[newSymbol] = prev[oldSymbol] || { statusStarting: false, statusDivisor: false };
        if (oldSymbol && oldSymbol in nextState) {
          delete nextState[oldSymbol];
        }
        return nextState;
      });
    },
    [setSelectedIndex, setIndexMetadata, setEnableDivisorOrStartingValue],
  );

  const handleCalculationFrequencySelect = value => {
    const shouldChangeEffectiveDateToTomorrow =
      value === DATA_CALCULATION_SETUP_VALUES.EOD &&
      isDateBeforeOrToday(indexMetadata?.effective_date);
    if (shouldChangeEffectiveDateToTomorrow) {
      setIndexMetadata('effective_date', getDateString(1));
    }
    if (value === DATA_CALCULATION_SETUP_VALUES.EOD) {
      setIndexMetadata('launch_point', DATA_CALCULATION_SETUP_VALUES.EOD);
    }
    setIndexMetadata('calculation_setup', value);
  };

  const handleWithholdingTaxSelect = value => {
    setIndexMetadata('withholding_tax_id', value);
  };

  const handleOrgPrefixChange = e => {
    setIndexMetadata('org_prefix', e.target.value.toUpperCase());
  };

  const handleCalculationZoneSelect = value => {
    setIndexMetadata('calculation_zone', value);
  };

  const handleAutomaticallyAdjustDivisorChange = () => {
    setAutomaticallyAdjustDivisor(!automaticallyAdjustDivisor);
    setIndexMetadata('automatically_adjust_divisor', !automaticallyAdjustDivisor);
  };

  const handleLaunchPointSelect = value => {
    setIndexMetadata('launch_point', value?.value);
    if (value?.value === 'IMMEDIATELY') {
      setIndexMetadata('launch_date', getDateString());
    }
  };

  const handleStartingValueSwitcher = symbol => {
    setEnableDivisorOrStartingValue({
      ...enableDivisorOrStartingValue,
      [symbol]: {
        statusStarting: !enableDivisorOrStartingValue?.[symbol]?.statusStarting,
        statusDivisor: !enableDivisorOrStartingValue?.[symbol]?.statusDivisor,
      },
    });
  };

  const handleDivisorSwitcher = symbol => {
    setEnableDivisorOrStartingValue({
      ...enableDivisorOrStartingValue,
      [symbol]: {
        statusStarting: !enableDivisorOrStartingValue?.[symbol]?.statusStarting,
        statusDivisor: !enableDivisorOrStartingValue?.[symbol]?.statusDivisor,
      },
    });
  };

  const handleDivisorDeterminationSetupSelect = value => {
    if (value?.value === 'IMMEDIATELY') setIndexMetadata('launch_point', '');
    setIndexMetadata('divisor_determination_setup', value?.id);
  };

  const handleDivisorChange = val => {
    setIndexMetadata('divisor', parseDecimalInputValue(val));
  };

  const handleStartingValueChange = val => {
    setIndexMetadata('starting_value', parseDecimalInputValue(val));
  };

  return (
    <div className={styles.parametersLeftContainer}>
      <div className={styles.parametersLgInputContainer}>
        <div className={styles.parametersTitleInput}>NAME</div>
        <input
          id="field-name"
          ref={registerFieldRef('name')}
          className={`${styles.parametersInput} ${styles.parametersInputMarginBottom} ${
            errors[indexMetadata?.symbol]?.name ? styles.parametersInputError : ''
          }`}
          onChange={validateAndHandleNameChange}
          value={indexMetadata?.name || ''}
          placeholder="Enter Name"
        />
        {errors[indexMetadata?.symbol]?.name && (
          <div className={styles.errorMessageStaticPosition}>
            {errors[indexMetadata?.symbol]?.name}
          </div>
        )}
      </div>
      <div className={styles.parametersLgInputContainer}>
        <div className={styles.parametersTitleInput}>{FORM_INPUT_TEXTS.labels.description}</div>
        <input
          id="field-description"
          ref={registerFieldRef('description')}
          as="textarea"
          rows={3}
          style={{ resize: 'none' }}
          placeholder={FORM_INPUT_TEXTS.placeholders.descriptions}
          className={`${styles.parametersInput} ${styles.parametersInputMarginBottom} ${
            errors[indexMetadata?.symbol]?.description ? styles.parametersInputError : ''
          }`}
          onChange={handleDescriptionChange}
          value={indexMetadata?.description ?? ''}
        />
        {errors[indexMetadata?.symbol]?.description && (
          <div className={styles.errorMessageStaticPosition}>
            {errors[indexMetadata?.symbol]?.description}
          </div>
        )}
      </div>

      <div className={styles.columnsInputContainer}>
        <div className={styles.columnsInputRowContainer}>
          {isSubmitBasket && (
            <>
              <div className={styles.parametersMdInputContainer}>
                <div className={styles.parametersTitleInput}>Org. Prefix</div>
                <input
                  className={`${styles.parametersInput} ${styles.parametersInputMarginBottom}`}
                  value={indexMetadata?.org_prefix || ''}
                  placeholder="Enter Org. Prefix"
                  onChange={handleOrgPrefixChange}
                />
              </div>
            </>
          )}
          <div
            className={`${styles.parametersMdInputContainer} ${
              !isSubmitBasket ? styles.parametersMdInputContainerReadOnly : ''
            }`}
          >
            <div className={styles.parametersTitleInput}>TICKER</div>
            <input
              ref={el => {
                tickerInputRef.current = el;
                registerFieldRef('symbol')(el);
              }}
              className={`${styles.parametersInput} ${styles.parametersInputMarginBottom} ${
                errors[indexMetadata?.symbol]?.symbol ? styles.parametersInputError : ''
              }`}
              onChange={e =>
                handleTickerChange(indexMetadata?.symbol, e.target.value?.toUpperCase())
              }
              value={indexMetadata?.symbol || ''}
              placeholder="Enter Ticker"
              id="field-symbol"
            />
            {errors[indexMetadata?.symbol]?.symbol && (
              <div className={styles.errorMessageStaticPosition}>
                {errors[indexMetadata?.symbol]?.symbol}
              </div>
            )}
          </div>
        </div>

        {/* Automatically Adjust Divisor Section */}
        {!isSubmitBasket && (
          <div className={styles.columnsInputRowContainer}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '18px',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <div>AUTOMATICALLY ADJUST DIVISOR</div>
              <div>
                <Switcher.Switch
                  className={styles.customRadio}
                  checked={automaticallyAdjustDivisor}
                  onChange={handleAutomaticallyAdjustDivisorChange}
                />
              </div>
            </div>
          </div>
        )}

        {isSubmitBasket && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <div
                className={portforlioStyles.contentInput}
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  marginRight: '40px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '18px',
                    width: 'auto',
                  }}
                >
                  <div>Starting Value</div>
                  <div>
                    <Switcher.Switch
                      className={indexBuilderStyles.customRadio}
                      onChange={() => handleStartingValueSwitcher(indexMetadata?.symbol)}
                      checked={
                        enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusStarting
                      }
                    />
                  </div>
                </div>
                {enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusStarting && (
                  <BitaInput
                    id="field-starting_value"
                    ref={registerFieldRef('starting_value')}
                    value={indexMetadata?.starting_value}
                    type="number"
                    inputStyle={
                      !indexMetadata?.starting_value
                        ? portforlioStyles.needValidation
                        : portforlioStyles.basketInput
                    }
                    title="Start Value"
                    placeholder="Enter Value"
                    lower_limit={0}
                    handleChange={val => handleStartingValueChange(val)}
                  />
                )}
              </div>
              {enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusStarting && (
                <div
                  id="field-divisor_determination_setup"
                  ref={registerFieldRef('divisor_determination_setup')}
                >
                  <BitaSelect
                    selected={indexMetadata?.divisor_determination_setup}
                    value={divisorDeterminationSetupValue}
                    label="Select a Divisor Determination Point"
                    externalPlaceholder="Divisor Determination Point"
                    data={DATA_DIVISOR_DETERMINATION_SETUP}
                    handleSelection={handleDivisorDeterminationSetupSelect}
                    selectClassname={
                      !validateFields(
                        'divisor_determination_setup',
                        indexMetadata?.divisor_determination_setup,
                      ) && portforlioStyles.needValidationSelect
                    }
                  />
                </div>
              )}
            </div>
            <div
              className={portforlioStyles.contentInput}
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-start',
                alignItems: 'baseline',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '18px',
                }}
              >
                <div>Divisor</div>
                <div>
                  <Switcher.Switch
                    className={indexBuilderStyles.customRadio}
                    onChange={() => handleDivisorSwitcher(indexMetadata?.symbol)}
                    checked={enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusDivisor}
                  />
                </div>
              </div>

              {enableDivisorOrStartingValue?.[indexMetadata?.symbol]?.statusDivisor && (
                <BitaInput
                  id="field-divisor"
                  ref={registerFieldRef('divisor')}
                  value={indexMetadata?.divisor}
                  type="number"
                  inputStyle={
                    !indexMetadata?.divisor
                      ? portforlioStyles.needValidation
                      : portforlioStyles.basketInput
                  }
                  title="Divisor"
                  placeholder="Enter Value"
                  lower_limit={0}
                  handleChange={val => handleDivisorChange(val)}
                />
              )}
            </div>
          </div>
        )}

        {!isSubmitBasket && (
          <div className={styles.columnsInputRowContainer}>
            {!automaticallyAdjustDivisor ? (
              <div className={styles.parametersLgInputContainer}>
                <div className={styles.parametersTitleInput}>DIVISOR</div>
                <input
                  id="field-divisor"
                  ref={registerFieldRef('divisor')}
                  className={`${styles.parametersInput} ${
                    !indexMetadata?.divisor ? styles.needValidation : ''
                  }`}
                  type="number"
                  value={indexMetadata?.divisor || ''}
                  placeholder="Enter Value"
                  onChange={e => {
                    setIndexMetadata('divisor', Number(e.target.value.replace(/,/g, '')));
                  }}
                />
                {errors[indexMetadata?.symbol]?.divisor && (
                  <div className={`${styles.errorMessageStaticPosition} ${styles.marginTopSmall}`}>
                    {errors[indexMetadata?.symbol]?.divisor}
                  </div>
                )}
              </div>
            ) : (
              <div
                className={styles.divisorDeterminationContainer}
                id="field-divisor_determination_setup"
                ref={registerFieldRef('divisor_determination_setup')}
              >
                <BitaSelect
                  warningIcon
                  selected={indexMetadata?.divisor_determination_setup}
                  value={divisorDeterminationSetupValue}
                  label="Select a Divisor Determination Point"
                  externalPlaceholder="DIVISOR DETERMINATION POINT"
                  data={DATA_DIVISOR_DETERMINATION_SETUP}
                  handleSelection={handleDivisorDeterminationSetupSelect}
                  selectClassname={
                    !validateFields(
                      'divisor_determination_setup',
                      indexMetadata?.divisor_determination_setup,
                    ) && styles.needValidationSelect
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className={styles.columnsInputRowContainer}>
          <div className={styles.parametersPickerContainerFlex}>
            <div className={styles.parametersPickerContainer}>
              <div className={styles.parametersTitleInput}>CURRENCY</div>
              <div id="field-currency" ref={registerFieldRef('currency')}>
                <BitaSelect
                  width="100%"
                  selected={indexMetadata?.currency}
                  value={currencyValue}
                  label="Select a Currency"
                  data={currenciesArray}
                  handleSelection={value => handleCurrencySelect(value?.id)}
                />
              </div>
              {errors[indexMetadata?.symbol]?.currency && (
                <div className={styles.errorMessageStaticPosition}>
                  {errors[indexMetadata?.symbol]?.currency}
                </div>
              )}
            </div>
            <div className={styles.parametersPickerContainer}>
              <div className={styles.parametersTitleInput}>RETURN TYPE</div>
              <div id="field-calculation_type" ref={registerFieldRef('calculation_type')}>
                <BitaSelect
                  width="100%"
                  selected={indexMetadata?.calculation_type}
                  value={calculationTypeValue}
                  label="Select a Return Type"
                  data={BULK_CALCULATION_TYPE_OPTIONS}
                  handleSelection={value => handleReturnTypeSelect(value?.id)}
                />
              </div>
              {errors[indexMetadata?.symbol]?.calculation_type && (
                <div className={styles.errorMessageStaticPosition}>
                  {errors[indexMetadata?.symbol]?.calculation_type}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.columnsInputRowContainer}>
          {indexMetadata?.calculation_type === 'NET_RETURN' && (
            <>
              <div className={styles.parametersPickerContainer}>
                <div className={styles.parametersTitleInput}>WITHHOLDING TAXES TEMPLATE</div>
                <BitaSelect
                  width="100%"
                  warningIcon
                  selected={
                    indexMetadata?.withholding_tax_id ?? BITA_WITHHOLDING_TAX.withholding_tax_name
                  }
                  data={withholdingTaxTemplateData?.map(template => {
                    return { value: template?.withholding_tax_name, id: template?.id };
                  })}
                  value={withholdingTaxIdValue}
                  handleSelection={value => handleWithholdingTaxSelect(value?.id)}
                />
              </div>
              <div className={styles.parametersPickerContainer}>
                <div className={styles.parametersTitleInput}>&nbsp;</div>
                <div className={styles.withholdingTaxButtonContainer}>
                  <BitaButton
                    width="100%"
                    height="35px"
                    className={styles.withholdingTaxButton}
                    onClick={() => {
                      setIsWithholdingModalOpen(!isWithholdingModalOpen);
                    }}
                  >
                    Manage Withholding Taxes
                  </BitaButton>
                </div>
              </div>
            </>
          )}
        </div>

        {isSubmitBasket && (
          <div className={styles.columnsInputRowContainer}>
            <div className={styles.parametersPickerContainerFlex}>
              {indexMetadata?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY && (
                <div
                  className={
                    shouldSendLaunchDate
                      ? styles.parametersPickerContainer
                      : styles.parametersPickerContainerHalf
                  }
                >
                  <div className={styles.parametersTitleInput}>LAUNCH POINT</div>
                  <div id="field-launch_point" ref={registerFieldRef('launch_point')}>
                    <BitaSelect
                      width="100%"
                      selected={indexMetadata?.launch_point}
                      label="Select a Launch Point"
                      value={launchPointValue}
                      data={launchPointData}
                      handleSelection={handleLaunchPointSelect}
                    />
                  </div>
                  {errors[indexMetadata?.symbol]?.launch_point && (
                    <div className={styles.errorMessageStaticPosition}>
                      {errors[indexMetadata?.symbol]?.launch_point}
                    </div>
                  )}
                </div>
              )}
              {shouldSendLaunchDate && (
                <div
                  className={
                    indexMetadata?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY
                      ? styles.parametersPickerContainer
                      : styles.parametersPickerContainerHalf
                  }
                >
                  <div className={styles.parametersTitleInput}>LAUNCH DATE</div>
                  <div id="field-launch_date" ref={registerFieldRef('launch_date')}>
                    <BitaCalendarDate
                      select={value => {
                        setIndexMetadata('launch_date', formatDateCalendar(value));
                      }}
                      maxDate={MAX_LAUNCH_DATE}
                      minDate={new Date()}
                      marginTitle={false}
                      date={indexMetadata?.launch_date}
                      tooltip="Select today's date or a future date"
                    />
                  </div>
                  {errors[indexMetadata?.symbol]?.launch_date && (
                    <div className={styles.errorMessageStaticPosition}>
                      {errors[indexMetadata?.symbol]?.launch_date}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {existsCashConstituentsInMetadata(indexMetadata?.symbol) && (
          <div className={styles.columnsInputRowContainer}>
            <div className={styles.parametersLgInputContainer}>
              <div className={styles.parametersTitleInput} style={{ whiteSpace: 'nowrap' }}>
                INTEREST RATE ON CASH
              </div>
              <input
                id="field-interest_rate_on_cash"
                ref={registerFieldRef('interest_rate_on_cash')}
                className={`${styles.parametersInput} ${styles.parametersInputMarginBottom}`}
                type="number"
                value={
                  indexMetadata?.additional_settings?.cash_constituent?.interest_rate_on_cash ?? ''
                }
                placeholder="Enter Value"
                onChange={e => {
                  setIndexMetadata('additional_settings', {
                    ...indexMetadata?.additional_settings,
                    cash_constituent: {
                      ...indexMetadata?.additional_settings?.cash_constituent,
                      interest_rate_on_cash: e.target.value,
                    },
                  });
                }}
              />
              {errors[indexMetadata?.symbol]?.interest_rate_on_cash && (
                <div className={styles.errorMessageStaticPosition}>
                  {errors[indexMetadata?.symbol]?.interest_rate_on_cash}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.columnsInputRowContainer}>
          <div className={styles.parametersPickerContainer}>
            <div className={styles.parametersTitleInput}>CALCULATION FREQUENCY</div>
            <div id="field-calculation_setup" ref={registerFieldRef('calculation_setup')}>
              <BitaSelect
                width="100%"
                selected={indexMetadata?.calculation_setup}
                value={calculationSetupValue}
                label="Select a Calculation Frequency"
                data={DATA_CALCULATION_SETUP}
                handleSelection={value => handleCalculationFrequencySelect(value?.id)}
              />
            </div>

            {errors[indexMetadata?.symbol]?.calculation_setup && (
              <div className={styles.errorMessageStaticPosition}>
                {errors[indexMetadata?.symbol]?.calculation_setup}
              </div>
            )}
          </div>
          <div className={styles.parametersPickerContainer}>
            <div className={styles.parametersTitleInput}>CALCULATION ZONE</div>
            <div id="field-calculation_zone" ref={registerFieldRef('calculation_zone')}>
              <BitaSelect
                width="100%"
                selected={indexMetadata?.calculation_zone}
                value={calculationZoneValue}
                label="Select a Calculation Zone"
                data={calculationZoneOptions}
                handleSelection={value => handleCalculationZoneSelect(value?.id)}
              />
            </div>

            {errors[indexMetadata?.symbol]?.calculation_zone && (
              <div className={styles.errorMessageStaticPosition}>
                {errors[indexMetadata?.symbol]?.calculation_zone}
              </div>
            )}
          </div>
        </div>

        {/* Annualized Daily Decrement Section */}
        <div className={styles.columnsInputRowContainer}>
          <div className={styles.parametersLgInputContainer}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '18px',
                alignItems: 'center',
                width: '100%',
                marginTop: '12px',
              }}
            >
              <div className={styles.parametersTitleInput}>ANNUALIZED DAILY DECREMENT</div>
              <div>
                <Switcher.Switch
                  className={styles.customRadio}
                  checked={indexMetadata?.additional_settings?.annualized_daily_decrement || false}
                  onChange={() => {
                    setIndexMetadata('additional_settings', {
                      ...indexMetadata?.additional_settings,
                      annualized_daily_decrement: !indexMetadata?.additional_settings
                        ?.annualized_daily_decrement,
                      decrement_value_percentage: null,
                    });
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.parametersPickerContainer}>
            <div />
          </div>
        </div>

        {/* Percentage Input Section */}
        {indexMetadata?.additional_settings?.annualized_daily_decrement && (
          <div className={styles.columnsInputRowContainer}>
            <div className={styles.parametersLgInputContainer}>
              <div
                className={`${styles.parametersTitleInput} ${styles.parametersTitleInputMargin}`}
              >
                PERCENTAGE
              </div>
              <BitaInput
                id="field-decrement_value_percentage"
                ref={registerFieldRef('decrement_value_percentage')}
                value={decrementValuePercentageValue}
                type="number"
                upper_limit={100}
                lower_limit={0}
                inputStyle={
                  !indexMetadata?.additional_settings?.decrement_value_percentage
                    ? styles.needValidation
                    : styles.parametersInput
                }
                placeholder="Enter Percentage"
                tooltip="Please indicate a percentage between 0 and 100"
                handleChange={val => {
                  const percentageValue = parseFloat(val) / 100;
                  setIndexMetadata('additional_settings', {
                    ...indexMetadata?.additional_settings,
                    decrement_value_percentage: percentageValue,
                  });
                }}
              />
            </div>
            <div className={styles.parametersPickerContainer}>
              <div style={{ marginTop: '15px', marginLeft: '30px' }}>
                Day-Count Conversion: 360 Days
              </div>
            </div>
          </div>
        )}
      </div>
      <VisualizationModal
        withholdingTaxTemplateData={[
          { ...BITA_WITHHOLDING_TAX, id: 'default' },
          ...withholdingTaxTemplateData,
        ]}
        updateTemplate={() => {
          updateWithholdingTaxTemplatesData(user, setWithholdingTaxTemplateData);
        }}
        select={value => {
          handleWithholdingTaxSelect(value);
          updateWithholdingTaxTemplatesData(user, setWithholdingTaxTemplateData);
        }}
        setModalState={setIsWithholdingModalOpen}
        isModalOpen={isWithholdingModalOpen}
      />
    </div>
  );
};

export default ParametersForm;
